import { ChevronDown, ChevronRight } from "lucide-react";
import { PRODUCT_COL_WIDTH } from "@/lib/gridLayout";
import type { HealthColumn } from "./StockHealthCells";
import ColumnInfo from "./ColumnInfo";
import {
  isSortColumn,
  nextSort,
  type MatrixSort,
  type SortDirection,
} from "../lib/stockHealth";
import type { MatrixColumn, MatrixLayout } from "../lib/matrixColumns";

export const TOTAL_COL_WIDTH = 88;
// Wide enough that a one-warehouse channel band still fits its label
// (e.g. "MARKETPLACE" over a single column).
export const COL_WIDTH = 104;
const BAND_HEIGHT = 24;
const NAME_HEIGHT = 40;

export interface WarehouseColumn {
  external_id: string;
  name: string | null;
  code: string | null;
  channel_id: string | null;
  channel_name: string | null;
}

export function warehouseLabel(w: WarehouseColumn): string {
  return w.name ?? w.code ?? w.external_id;
}

// Name-row label for a column; a folded channel says how many warehouses it adds up.
function columnLabel(c: MatrixColumn<WarehouseColumn>): string {
  return c.kind === "warehouse" ? warehouseLabel(c.warehouse) : `${c.warehouses.length} warehouses`;
}

function columnHint(c: MatrixColumn<WarehouseColumn>): string {
  if (c.kind === "warehouse") {
    return [warehouseLabel(c.warehouse), c.warehouse.channel_name].filter(Boolean).join(" · ");
  }
  return `${c.channelName ?? "Channel"} total: ${c.warehouses.map(warehouseLabel).join(", ")}`;
}

export function cellBorder(isGroupEnd: boolean): string {
  return isGroupEnd ? "2px solid var(--border)" : "1px solid var(--border)";
}

interface Props {
  layout: MatrixLayout<WarehouseColumn>;
  onToggleChannel: (channelId: string) => void;
  healthColumns: HealthColumn[];
  sidebarOpen: boolean;
  sort: MatrixSort;
  onSortChange: (sort: MatrixSort) => void;
}

const SORT_ARROW: Record<SortDirection | "none", string> = {
  none: "↕",
  normal: "↓",
  reversed: "↑",
};

const STATUS_SORT_HINT: Record<SortDirection | "none", string> = {
  none: "Click to show the most urgent first",
  normal: "Most urgent first. Click to show the least urgent first",
  reversed: "Least urgent first. Click to go back to the default order",
};

const VALUE_SORT_HINT: Record<SortDirection | "none", string> = {
  none: "Click to show the highest value first",
  normal: "Highest value first. Click to show the lowest value first",
  reversed: "Lowest value first. Click to go back to the default order",
};

const DAYS_SORT_HINT: Record<SortDirection | "none", string> = {
  none: "Click to show the fewest days first",
  normal: "Fewest days first. Click to show the most days first",
  reversed: "Most days first. Click to go back to the default order",
};

// Two stacked sticky rows: a channel band spanning each channel's warehouses,
// then the warehouse names. A channel with several warehouses opens and closes
// from its band. The product + total cells pin to the left edge.
export default function StockMatrixHeader({
  layout,
  onToggleChannel,
  healthColumns,
  sidebarOpen,
  sort,
  onSortChange,
}: Props) {
  const healthWidth = healthColumns.reduce((sum, c) => sum + c.width, 0);
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: sidebarOpen ? 46 : 3,
        borderBottom: "2px solid var(--border)",
      }}
    >
      <div style={{ display: "flex", height: BAND_HEIGHT }}>
        <div
          style={{
            width: PRODUCT_COL_WIDTH + TOTAL_COL_WIDTH,
            flexShrink: 0,
            position: "sticky",
            left: 0,
            zIndex: 4,
            background: "var(--muted)",
            borderRight: "2px solid var(--border)",
            borderBottom: "1px solid var(--border)",
          }}
        />
        <div
          style={{
            width: healthWidth,
            flexShrink: 0,
            background: "var(--muted)",
            borderRight: "2px solid var(--border)",
            borderBottom: "1px solid var(--border)",
          }}
        />
        {layout.bands.map((g, idx) => (
          <div
            key={`${g.channelId}-${idx}`}
            {...(g.foldable && {
              onClick: () => onToggleChannel(g.channelId!),
              role: "button",
            })}
            // A band that opens and closes reads darker than a plain one and
            // dims slightly on hover so it looks clickable (the inline
            // background would override a hover background class).
            className={`text-[10px] font-semibold uppercase tracking-wider ${
              g.foldable
                ? "cursor-pointer select-none text-foreground hover:brightness-95"
                : "text-muted-foreground"
            }`}
            // Grows by its column count so it stays exactly as wide as the
            // warehouse columns under it, which each grow equally.
            style={{
              flex: `${g.columnCount} 0 ${g.columnCount * COL_WIDTH}px`,
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "0 8px",
              background: "color-mix(in srgb, var(--primary) 4%, var(--muted))",
              borderRight: "2px solid var(--border)",
              borderBottom: "1px solid var(--border)",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
            title={
              g.foldable
                ? `${g.name ?? ""} · Click to ${g.open ? "fold into one total" : "show each warehouse"}`
                : (g.name ?? undefined)
            }
          >
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{g.name}</span>
            {g.foldable &&
              (g.open ? (
                <ChevronDown className="size-4 shrink-0" strokeWidth={2.5} />
              ) : (
                <ChevronRight className="size-4 shrink-0" strokeWidth={2.5} />
              ))}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", height: NAME_HEIGHT }}>
        <div
          style={{
            width: PRODUCT_COL_WIDTH,
            flexShrink: 0,
            position: "sticky",
            left: 0,
            zIndex: 4,
            background: "var(--muted)",
            display: "flex",
            alignItems: "center",
            padding: "0 12px",
            color: "var(--muted-foreground)",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          Product
        </div>
        <div
          style={{
            width: TOTAL_COL_WIDTH,
            flexShrink: 0,
            position: "sticky",
            left: PRODUCT_COL_WIDTH,
            zIndex: 4,
            background: "var(--muted)",
            borderRight: "2px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "0 8px",
            color: "var(--foreground)",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          Total
        </div>
        {healthColumns.map((c, i) => {
          const sortColumn = isSortColumn(c.key) ? c.key : null;
          const direction = sort?.column === c.key ? sort.direction : "none";
          const hint =
            c.key === "status" ? STATUS_SORT_HINT : c.key === "value" ? VALUE_SORT_HINT : DAYS_SORT_HINT;
          return (
            <div
              key={c.key}
              {...(sortColumn && {
                onClick: () => onSortChange(nextSort(sort, sortColumn)),
                className: "cursor-pointer select-none",
                title: hint[direction],
              })}
              style={{
                width: c.width,
                flexShrink: 0,
                background: "var(--muted)",
                borderRight:
                  i === healthColumns.length - 1 ? "2px solid var(--border)" : "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                textAlign: "right",
                padding: "0 8px",
                color: "var(--foreground)",
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.25,
              }}
            >
              <span>
                {c.label}
                {/* The (i) opens its explanation without changing the sort. */}
                <span onClick={(e) => e.stopPropagation()}>
                  <ColumnInfo columnKey={c.key} />
                </span>
                {sortColumn && <span className="ml-1 text-xs">{SORT_ARROW[direction]}</span>}
              </span>
            </div>
          );
        })}
        {layout.columns.map((c) => (
          <div
            key={c.key}
            style={{
              flex: `1 0 ${COL_WIDTH}px`,
              background: "var(--muted)",
              borderRight: cellBorder(layout.bandEndKeys.has(c.key)),
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              textAlign: "right",
              padding: "0 8px",
              color: "var(--muted-foreground)",
              fontSize: 10,
              lineHeight: 1.25,
            }}
            title={columnHint(c)}
          >
            <span
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
            >
              {columnLabel(c)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
